/**
 * InitiativeGoalsPanel - Reusable panel for Initiative Goals management
 * 
 * Used in:
 * - InitiativeDetailModal (edit mode)
 * - InitiativeMergeModal (source view + merged form)
 * 
 * Props:
 * - linkedGoalIds: array of goal IDs currently linked
 * - allGoals: array of all available goals for linking
 * - onLink: (goalId) => void
 * - onUnlink: (goalId) => void
 * - isEditing: boolean (controls whether goals can be linked/unlinked)
 * - colors: theme colors from useSafeMode
 * - mode: 'edit' | 'create' | 'view'
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { Search, Target, Plus, X } from 'lucide-react';

const InitiativeGoalsPanel = ({
  linkedGoalIds = [],
  allGoals = [],
  onLink,
  onUnlink,
  isEditing = true,
  colors,
  mode = 'edit',
}) => {
  // Goal search state
  const [goalSearch, setGoalSearch] = useState('');
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  
  // Ref for dropdown click-outside handling
  const goalDropdownRef = useRef(null);
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (goalDropdownRef.current && !goalDropdownRef.current.contains(event.target)) {
        setShowGoalDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLinkGoal = (goalId) => {
    if (onLink) {
      onLink(goalId);
    }
    setGoalSearch('');
    setShowGoalDropdown(false);
  };

  const handleUnlinkGoal = (goalId) => {
    if (onUnlink) {
      onUnlink(goalId);
    }
  };

  // Filter goals that aren't already linked
  const availableGoals = allGoals.filter(goal => 
    !linkedGoalIds.includes(goal.goal_id) &&
    (!goalSearch || goal.title?.toLowerCase().includes(goalSearch.toLowerCase()))
  );

  // Get linked goals with full data
  const linkedGoals = linkedGoalIds.map(gid => {
    const goal = allGoals.find(g => g.goal_id === gid);
    return goal || { goal_id: gid, title: 'Unknown Goal' };
  });

  return (
    <div className="space-y-4">
      {/* Search and Link Goals - Only when editing */}
      {isEditing && mode !== 'view' && (
        <div className="relative" ref={goalDropdownRef}>
          <Label className={cn("mb-1 block", colors.textSecondary)}>
            Link Goal
          </Label>
          <div className="relative">
            <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
            <Input
              type="text"
              placeholder="Search and select a goal to link..."
              value={goalSearch}
              onChange={(e) => {
                setGoalSearch(e.target.value);
                setShowGoalDropdown(true);
              }}
              onFocus={() => setShowGoalDropdown(true)}
              className={cn("pl-10", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              data-testid="goal-search-input"
            />
          </div>
          
          {/* Goal Dropdown */}
          {showGoalDropdown && (
            <div className={cn(
              "absolute z-50 w-full mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg",
              colors.bgCard, colors.borderPrimary
            )}>
              {availableGoals.length > 0 ? (
                availableGoals.map(goal => (
                  <button
                    key={goal.goal_id}
                    onClick={() => handleLinkGoal(goal.goal_id)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                      colors.textPrimary
                    )}
                  >
                    <Target className="w-4 h-4 text-indigo-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{goal.title}</p>
                      <p className={cn("text-xs", colors.textMuted)}>
                        {goal.kpis?.length || 0} metrics
                      </p>
                    </div>
                    <Plus className={cn("w-4 h-4", colors.textMuted)} />
                  </button>
                ))
              ) : (
                <div className={cn("p-4 text-center", colors.textMuted)}>
                  <p className="text-sm">
                    {linkedGoalIds.length > 0 && availableGoals.length === 0 
                      ? 'All available goals are already linked'
                      : 'No goals found'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Linked Goals List */}
      {linkedGoals.length > 0 ? (
        <div className="space-y-2">
          {linkedGoals.map(goal => (
            <div 
              key={goal.goal_id}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg border",
                colors.bgTertiary, colors.borderPrimary
              )}
              data-testid={`linked-goal-${goal.goal_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", "bg-indigo-100 dark:bg-indigo-900/30")}>
                  <Target className="w-4 h-4 text-indigo-500" />
                </div>
                <div>
                  <p className={cn("font-medium", colors.textPrimary)}>
                    {goal.title || 'Linked Goal'}
                  </p>
                  <p className={cn("text-xs", colors.textMuted)}>
                    {goal.kpis?.length || 0} metrics available
                  </p>
                </div>
              </div>
              {isEditing && mode !== 'view' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleUnlinkGoal(goal.goal_id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Unlink goal"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={cn("p-6 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
          <Target className={cn("w-10 h-10 mx-auto mb-2 opacity-40", colors.textMuted)} />
          <p className={cn("font-medium", colors.textSecondary)}>No goals linked yet</p>
          <p className={cn("text-sm mt-1", colors.textMuted)}>
            Link at least one Goal to set metric targets and track outcomes
          </p>
        </div>
      )}
      
      {/* Info Box */}
      {linkedGoals.length > 0 && mode !== 'view' && (
        <div className={cn("p-3 rounded-lg border", "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800")}>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>💡 What happens next?</strong> Go to the KPIs tab to select which metrics 
            from these Goals this Initiative will commit to moving.
          </p>
        </div>
      )}
    </div>
  );
};

export default InitiativeGoalsPanel;
